import { Link } from 'react-router-dom';
import { Minus, Plus, X, ShoppingBag, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCart } from '@/context/CartContext';
import { useSeo } from '@/hooks/useSeo';
import { KlarnaBadge } from '@/components/KlarnaBadge';

export default function CartPage() {
  useSeo({
    title: 'Il tuo Carrello',
    description: 'Rivedi i gioielli selezionati e procedi al checkout sicuro. Spedizione gratuita per ordini Ciro Gallotti.',
    path: '/carrello',
    noindex: true,
  });

  const { cart, loading, updateQuantity, removeFromCart } = useCart();

  const formatPrice = (price) =>
    new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
    }).format(price);

  if (loading) {
    return (
      <div className="bg-ivory pt-24 md:pt-28 min-h-screen" data-testid="cart-loading">
        <div className="max-w-editorial mx-auto px-6 md:px-12 py-12">
          <div className="animate-pulse space-y-6">
            <div className="h-10 bg-line w-48" />
            <div className="h-32 bg-line" />
            <div className="h-32 bg-line" />
          </div>
        </div>
      </div>
    );
  }

  const isEmpty = !cart?.items || cart.items.length === 0;

  return (
    <div className="bg-ivory pt-24 md:pt-28 min-h-screen" data-testid="cart-page">
      <header className="px-6 md:px-12 lg:px-16 py-12 md:py-16 hairline-b">
        <div className="max-w-editorial mx-auto">
          <p className="text-eyebrow text-bordeaux mb-3">Riepilogo</p>
          <h1 className="font-display text-h1 text-ink">Carrello</h1>
        </div>
      </header>

      <div className="max-w-editorial mx-auto px-6 md:px-12 py-12 md:py-16">
        {isEmpty ? (
          <div className="text-center py-20" data-testid="cart-empty">
            <div className="w-16 h-16 border border-line rounded-full flex items-center justify-center mx-auto mb-8">
              <ShoppingBag className="w-7 h-7 text-muted" strokeWidth={1.25} />
            </div>
            <h2 className="font-display text-h3 text-ink mb-4">Il tuo carrello è vuoto</h2>
            <p className="text-muted mb-10 leading-relaxed">
              Scopri la nostra collezione di gioielli unici realizzati a Napoli.
            </p>
            <Link to="/collezione" data-testid="continue-shopping">
              <Button size="lg" variant="accent">
                Vai alla Collezione
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-7 xl:col-span-8 space-y-6">
              {cart.items.map((item) => (
                <article
                  key={`${item.product_id}-${item.size || ''}`}
                  className="flex gap-5 md:gap-6 py-6 hairline-b"
                  data-testid={`cart-item-${item.product_id}${item.size ? `-${item.size.replace(' ', '')}` : ''}`}
                >
                  <Link
                    to={`/prodotto/${item.product_id}`}
                    className="flex-shrink-0 w-24 h-32 md:w-28 md:h-36 overflow-hidden bg-secondary rounded-2xl"
                  >
                    <img
                      src={item.product?.image_url}
                      alt={item.product?.name}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-700 ease-out"
                      loading="lazy"
                    />
                  </Link>

                  <div className="flex-1 flex flex-col">
                    <div className="flex justify-between gap-4">
                      <div>
                        <p className="text-eyebrow text-bordeaux mb-1">{item.product?.category}</p>
                        <Link
                          to={`/prodotto/${item.product_id}`}
                          className="font-display text-h4 text-ink hover:text-bordeaux transition-colors duration-hover"
                        >
                          {item.product?.name}
                        </Link>
                        {item.size && (
                          <p className="text-small text-muted mt-1" data-testid={`cart-item-size-${item.product_id}`}>
                            Misura: <span className="text-ink">{item.size}</span>
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => removeFromCart(item.product_id, item.size)}
                        className="w-9 h-9 border border-line text-ink hover:border-bordeaux hover:bg-bordeaux hover:text-ivory flex items-center justify-center transition-colors duration-hover rounded-full flex-shrink-0"
                        data-testid={`remove-item-${item.product_id}`}
                        aria-label="Rimuovi dal carrello"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="mt-auto flex items-center justify-between">
                      <div className="flex items-center border border-line rounded-full">
                        <button
                          onClick={() => updateQuantity(item.product_id, item.quantity - 1, item.size)}
                          className="w-9 h-9 flex items-center justify-center text-ink hover:bg-secondary transition-colors duration-hover"
                          data-testid={`decrease-${item.product_id}`}
                          aria-label="Diminuisci quantità"
                        >
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <span className="w-10 text-center text-sm font-medium tabular-nums">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.product_id, item.quantity + 1, item.size)}
                          className="w-9 h-9 flex items-center justify-center text-ink hover:bg-secondary transition-colors duration-hover"
                          data-testid={`increase-${item.product_id}`}
                          aria-label="Aumenta quantità"
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>

                      <p className="font-display text-h4 text-ink tabular-nums">
                        {formatPrice(item.item_total)}
                      </p>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            <aside className="lg:col-span-5 xl:col-span-4">
              <div className="border border-line p-8 sticky top-28">
                <h2 className="font-display text-h3 text-ink mb-8">Riepilogo Ordine</h2>

                <dl className="space-y-3 mb-6 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-muted">Subtotale</dt>
                    <dd className="text-ink font-medium tabular-nums">{formatPrice(cart.total)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-muted">Spedizione</dt>
                    <dd className="text-bordeaux font-medium">Gratuita</dd>
                  </div>
                </dl>

                <div className="hairline-t pt-6 mb-8">
                  <div className="flex justify-between items-baseline">
                    <span className="text-eyebrow text-ink">Totale</span>
                    <span
                      className="font-display text-h3 text-ink tabular-nums"
                      data-testid="cart-total"
                    >
                      {formatPrice(cart.total)}
                    </span>
                  </div>
                  <KlarnaBadge amount={cart.total} className="mt-3 justify-end" />
                </div>

                <Link to="/checkout" data-testid="proceed-to-checkout">
                  <Button size="lg" variant="accent" className="w-full">
                    Procedi al Checkout
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>

                <Link
                  to="/collezione"
                  className="block text-center text-small text-muted hover:text-bordeaux mt-6 transition-colors duration-hover"
                >
                  Continua lo Shopping
                </Link>
              </div>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}
